import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";

/**
 * Wave 26.7 — Centralised markdown renderer for ALL Hidro / AI-authored text.
 *
 * Why centralised? The Trust Contract requires bold/italic/headings/lists/
 * tables/code/links to render as actual formatting (not raw `**asterisks**`)
 * across every AI surface — chat bubbles, deep search output, trip plans,
 * engineering exports, library answers, etc.
 *
 * Usage:
 *     <HidroMarkdown text={message.text} />
 *
 * - Sanitised by ReactMarkdown by default (no raw HTML executed).
 * - GFM enabled (tables, strikethrough, task lists, autolinks).
 * - Inherits parent text colour but applies bolder weights for headings,
 *   adds spacing between blocks, and styles inline code + tables to match
 *   the dark CRM theme.
 *
 * Variants:
 *  - `compact` (default false): tighter line-height, smaller code blocks.
 *  - `printMode` (default false): switches to print-safe colours (used by
 *    JC/Ticket print modals so AI summaries don't render white-on-white).
 */
export default function HidroMarkdown({ text, compact = false, printMode = false, className = "", testId, components: extraComponents }) {
  const safeText = (text || "").trim();
  if (!safeText) return null;

  const baseTextColor = printMode ? "text-black" : "text-[#e4ecf4]";
  const headingColor = printMode ? "text-black" : "text-white";
  const codeBg = printMode ? "bg-black/5" : "bg-white/[0.06]";
  const codeBorder = printMode ? "border-black/15" : "border-white/10";
  const linkColor = printMode ? "text-[#0d6e75]" : "text-[#1FB6C1]";
  const tableBorder = printMode ? "border-black/20" : "border-white/15";
  const tableHeadBg = printMode ? "bg-black/5" : "bg-white/[0.04]";
  const blockquoteBorder = printMode ? "border-black/30" : "border-[#1FB6C1]/40";
  const blockquoteText = printMode ? "text-black/70" : "text-[#a0aabf]";
  const hrColor = printMode ? "border-black/15" : "border-white/10";

  const spacing = compact ? "space-y-2" : "space-y-3";
  const headingMy = compact ? "mt-3 mb-1" : "mt-4 mb-2";
  const lineHeight = compact ? "leading-relaxed" : "leading-relaxed";

  const baseComponents = {
          h1: ({ node, ...props }) => (
            <h1 className={`text-lg font-bold ${headingColor} ${headingMy} font-display`} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className={`text-base font-bold ${headingColor} ${headingMy} font-display`} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className={`text-sm font-bold ${headingColor} ${headingMy} uppercase tracking-wider`} {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className={`text-sm font-bold ${headingColor} ${headingMy}`} {...props} />
          ),
          p: ({ node, ...props }) => <p className="my-1.5" {...props} />,
          strong: ({ node, ...props }) => <strong className={`font-bold ${headingColor}`} {...props} />,
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
          li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
          a: ({ node, href = "", ...props }) => (
            href.startsWith("/") && !href.startsWith("/api/") ? (
              <Link to={href} className={`${linkColor} hover:underline`} {...props} />
            ) : (
              <a
                className={`${linkColor} hover:underline break-words`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              />
            )
          ),
          code: ({ node, inline, className: cls, children, ...props }) => {
            if (inline) {
              return (
                <code
                  className={`px-1 py-0.5 rounded text-[0.85em] ${codeBg} border ${codeBorder} font-mono ${linkColor}`}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code className={`block ${cls || ""}`} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              className={`my-2 p-2 rounded ${codeBg} border ${codeBorder} font-mono text-[12px] overflow-x-auto`}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className={`border-l-4 ${blockquoteBorder} pl-3 my-2 italic ${blockquoteText}`}
              {...props}
            />
          ),
          hr: () => <hr className={`my-3 ${hrColor}`} />,
          table: ({ node, ...props }) => (
            <div className="my-2 overflow-x-auto">
              <table className={`w-full text-xs border ${tableBorder} border-collapse`} {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className={tableHeadBg} {...props} />,
          th: ({ node, ...props }) => (
            <th className={`text-left px-2 py-1 border ${tableBorder} font-bold ${headingColor}`} {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className={`px-2 py-1 border ${tableBorder} align-top`} {...props} />
          ),
          input: ({ node, ...props }) => (
            // GFM task lists render as <input type="checkbox" /> — keep them
            // visually distinct + read-only so the user can't toggle them.
            <input
              className="accent-[#1FB6C1] mr-1.5 align-middle"
              {...props}
              disabled
              readOnly
            />
          ),
        };

  return (
    <div
      className={`${spacing} ${baseTextColor} ${lineHeight} ${className}`}
      data-testid={testId || "hidro-markdown"}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ ...baseComponents, ...(extraComponents || {}) }}
      >
        {safeText}
      </ReactMarkdown>
    </div>
  );
}
